/**
 * kbContentModel — vocabolari, macchina a stati e validazione per formato.
 *
 * Modulo PURO e CONDIVISO. È il GATE REALE della pubblicazione: le firestore
 * rules non possono fare validazione per-formato a costo ragionevole (e il
 * catch-all super_admin le scavalcherebbe comunque), quindi la regola vive qui
 * e viene invocata da DUE parti che non possono divergere:
 *  - la console, per dire all'editore cosa manca PRIMA di salvare;
 *  - la Cloud Function `pubblicaKbContenuto`, che è l'UNICA strada verso lo
 *    stato `pubblicato`.
 * Dichiarato apertamente invece di finger di gatarlo nelle rules.
 *
 * Doc canonico: docs/01-ARCHITECTURE/KB-CONTENT-MODEL.md
 */

import type {
  KbCanale,
  KbContenuto,
  KbFormato,
  KbFreschezza,
  KbGovernance,
  KbLivelloImpatto,
  KbScope,
  KbStato,
} from './kbTypes';

// ============================================================================
// VOCABOLARI RUNTIME
// ============================================================================

/**
 * I formati ammessi. **Tre, non sei.**
 * `faq`, `playbook` e `scheda_obiezioni` non si implementano finché nessuno li
 * chiede: il valore del modello unico è questa costante come punto di
 * estensione, non i formati inventati in anticipo.
 */
export const KB_FORMATI: readonly KbFormato[] = ['articolo', 'video', 'novita'] as const;

export const KB_STATI: readonly KbStato[] = [
  'bozza',
  'in_revisione',
  'pubblicato',
  'archiviato',
] as const;

export const KB_FRESCHEZZE: readonly KbFreschezza[] = [
  'attuale',
  'da_aggiornare',
  'obsoleto',
] as const;

export const KB_LIVELLI_IMPATTO: readonly KbLivelloImpatto[] = [
  'informativo',
  'rilevante',
  'importante',
  'critico',
] as const;

export const KB_SCOPES: readonly KbScope[] = ['piattaforma', 'azienda'] as const;

/**
 * Canali su cui un annuncio può viaggiare.
 *
 * In fase 1 si usa **solo `in_app`**: gli altri esistono nel vocabolario perché
 * il campo li accetta già, non perché siano implementati. La costante serve a
 * impedire che una stringa scritta male (`whatapp`) venga salvata e pubblichi
 * verso il nulla senza che nulla protesti — il tipo `KbCanale` da solo non
 * protegge, perché al confine con Firestore arriva una stringa qualunque.
 */
export const KB_CANALI: readonly KbCanale[] = [
  'in_app',
  'email',
  'whatsapp',
  'display_monitor',
] as const;

export const KB_CANALE_LABEL: Record<KbCanale, string> = {
  in_app: 'nel gestionale',
  email: 'email',
  whatsapp: 'WhatsApp',
  display_monitor: 'monitor in azienda',
};

/** Canali realmente attivi oggi. Gli altri si abilitano quando esistono davvero. */
export const KB_CANALI_ATTIVI: readonly KbCanale[] = ['in_app'] as const;

// ============================================================================
// ETICHETTE (italiano, mai Title Case inglese)
// ============================================================================

export const KB_FORMATO_LABEL: Record<KbFormato, string> = {
  articolo: 'articolo',
  video: 'video',
  novita: 'novità',
};

export const KB_STATO_LABEL: Record<KbStato, string> = {
  bozza: 'bozza',
  in_revisione: 'in revisione',
  pubblicato: 'pubblicato',
  archiviato: 'archiviato',
};

export const KB_FRESCHEZZA_LABEL: Record<KbFreschezza, string> = {
  attuale: 'allineato al gestionale',
  da_aggiornare: 'da aggiornare',
  obsoleto: 'obsoleto',
};

/** Avviso mostrato al lettore su un contenuto `da_aggiornare`. */
export const KB_AVVISO_DA_AGGIORNARE =
  'alcune schermate o passaggi potrebbero differire dalla versione attuale del gestionale';

export const KB_LIVELLO_LABEL: Record<KbLivelloImpatto, string> = {
  informativo: 'informativo',
  rilevante: 'rilevante',
  importante: 'importante',
  critico: 'critico',
};

/** Etichette per la scelta del tipo di modifica: parole, non gergo `major`/`minor`. */
export const KB_TIPO_MODIFICA_LABEL = {
  maggiore: 'aggiornamento del contenuto',
  minore: 'correzione di forma',
} as const;

// ============================================================================
// MACCHINA A STATI
// ============================================================================

/**
 * Transizioni ammesse. `archiviato` è reversibile a `bozza` (si riprende un
 * contenuto ritirato) ma non si torna mai direttamente da `archiviato` a
 * `pubblicato`: si ripassa dalla bozza e dalla validazione.
 */
const TRANSIZIONI: Record<KbStato, readonly KbStato[]> = {
  bozza: ['in_revisione', 'pubblicato', 'archiviato'],
  in_revisione: ['bozza', 'pubblicato', 'archiviato'],
  pubblicato: ['bozza', 'archiviato'],
  archiviato: ['bozza'],
};

export function transizioneAmmessa(da: KbStato, a: KbStato): boolean {
  if (da === a) return true;
  return (TRANSIZIONI[da] ?? []).includes(a);
}

// ============================================================================
// VALIDAZIONE
// ============================================================================

export interface KbErroreValidazione {
  campo: string;
  messaggio: string;
}

export interface KbEsitoValidazione {
  valido: boolean;
  errori: KbErroreValidazione[];
}

/** Lunghezza massima del riassunto: è ciò che entra in una card senza troncare male. */
export const KB_RIASSUNTO_MAX = 240;

/** Slug ammesso come doc id: minuscole, cifre e trattini. */
export const KB_SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Trasforma un titolo in slug candidato. Non garantisce unicità (quella la
 * verifica chi scrive, sul database): serve a proporre un id leggibile.
 */
export function slugDaTitolo(titolo: string): string {
  return titolo
    .normalize('NFD')
    // Rimuove i segni diacritici combinanti (à → a, ö → o).
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

/**
 * Valida un contenuto per il suo formato. `corpoMarkdown` va passato a parte
 * perché sulla testa non c'è (vive nella revisione).
 *
 * `perPubblicazione = false` valida ciò che serve per SALVARE una bozza (poco);
 * `true` valida ciò che serve per PUBBLICARE (tutto). Così l'editore può salvare
 * un lavoro incompleto senza combattere col validatore, ma non può mandare in
 * onda un contenuto rotto.
 */
export function validaContenuto(
  contenuto: Partial<KbContenuto>,
  opzioni: { corpoMarkdown?: string; perPubblicazione: boolean }
): KbEsitoValidazione {
  const errori: KbErroreValidazione[] = [];
  const { corpoMarkdown, perPubblicazione } = opzioni;

  const push = (campo: string, messaggio: string) => errori.push({ campo, messaggio });

  // — comune a tutti i formati —
  if (!contenuto.formato || !KB_FORMATI.includes(contenuto.formato)) {
    push('formato', `formato non valido: ammessi ${KB_FORMATI.join(', ')}`);
  }
  if (!contenuto.scope || !KB_SCOPES.includes(contenuto.scope)) {
    push('scope', 'scope non valido: ammessi piattaforma, azienda');
  }
  if (contenuto.scope === 'azienda' && !contenuto.tenant_id) {
    push('tenant_id', 'un contenuto di azienda deve avere il tenant_id');
  }
  if (contenuto.scope === 'piattaforma' && contenuto.tenant_id) {
    push('tenant_id', 'un contenuto di piattaforma non deve avere il tenant_id');
  }
  if (!contenuto.titolo || !contenuto.titolo.trim()) {
    push('titolo', 'il titolo è obbligatorio');
  }
  if (contenuto.riassunto && contenuto.riassunto.length > KB_RIASSUNTO_MAX) {
    push('riassunto', `il riassunto non può superare ${KB_RIASSUNTO_MAX} caratteri`);
  }
  if (contenuto.id && !KB_SLUG_REGEX.test(contenuto.id)) {
    push('id', 'l\'indirizzo può contenere solo minuscole, cifre e trattini');
  }

  if (!perPubblicazione) {
    return { valido: errori.length === 0, errori };
  }

  // — da qui in poi: requisiti per andare in onda —
  if (!contenuto.riassunto || !contenuto.riassunto.trim()) {
    push('riassunto', 'il riassunto è obbligatorio per pubblicare: è ciò che si legge nelle card');
  }
  if (!contenuto.owner_uid) {
    push('owner_uid', 'serve un responsabile del contenuto: senza owner nessuno lo aggiornerà');
  }
  if (!contenuto.motivo_ultima_modifica || !contenuto.motivo_ultima_modifica.trim()) {
    push('motivo_ultima_modifica', 'il motivo della modifica è obbligatorio');
  }
  if (!contenuto.aree_coperte || contenuto.aree_coperte.length === 0) {
    push(
      'aree_coperte',
      'indica almeno un\'area funzionale: è ciò che collega il contenuto ai rilasci futuri'
    );
  }
  if (!contenuto.audience || (!contenuto.audience.tutti_i_ruoli && !contenuto.audience.ruoli?.length)) {
    push('audience', 'indica a chi è destinato: tutti i ruoli oppure almeno un ruolo');
  }

  // — per formato —
  if (contenuto.formato === 'articolo' || contenuto.formato === 'novita') {
    if (!corpoMarkdown || !corpoMarkdown.trim()) {
      push('corpo_markdown', 'il testo è obbligatorio');
    }
  }
  if (contenuto.formato === 'video') {
    if (!contenuto.video?.storage_path) {
      push('video.storage_path', 'serve il file del video');
    }
  }
  if (contenuto.formato === 'novita') {
    const a = contenuto.annuncio;
    if (!a) {
      push('annuncio', 'una novità deve avere i dati dell\'annuncio');
    } else {
      if (!a.livello_impatto || !KB_LIVELLI_IMPATTO.includes(a.livello_impatto)) {
        push('annuncio.livello_impatto', 'livello di impatto non valido');
      }
      if (!a.canali || a.canali.length === 0) {
        push('annuncio.canali', 'indica almeno un canale');
      } else {
        const ignoti = a.canali.filter((c) => !KB_CANALI.includes(c));
        if (ignoti.length > 0) {
          push('annuncio.canali', `canale non riconosciuto: ${ignoti.join(', ')}`);
        }
      }
      if (a.etichetta_azione && !a.rotta_app) {
        push('annuncio.rotta_app', 'hai messo il testo del bottone ma non dove porta');
      }
      if (a.rotta_app && !a.rotta_app.startsWith('/')) {
        push('annuncio.rotta_app', 'la destinazione deve iniziare con "/"');
      }
    }
  } else if (contenuto.annuncio) {
    push('annuncio', 'i dati dell\'annuncio valgono solo per il formato novità');
  }

  return { valido: errori.length === 0, errori };
}

// ============================================================================
// GOVERNANCE — valori di partenza per il seed di `kb_settings/governance`
// ============================================================================

/**
 * Comportamento per livello di impatto. Sta nei DATI, non in un `if`: un quinto
 * livello domani non richiede un deploy.
 *
 * Nota su `forza_presa_visione`: anche il livello `critico` **non blocca** il
 * gestionale — resta in cima e non si chiude finché non si conferma. La versione
 * che impedisce di lavorare non esiste (decisione da prendere separatamente).
 */
export const KB_GOVERNANCE_DEFAULT: KbGovernance = {
  giorni_validita_default: 180,
  widget_max_novita: 3,
  widget_mostra_se_vuoto: false,
  livelli_impatto: {
    informativo: {
      ordine: 4,
      label: 'informativo',
      forza_presa_visione: false,
      canali_default: ['in_app'],
      giorni_evidenza: 14,
    },
    rilevante: {
      ordine: 3,
      label: 'rilevante',
      forza_presa_visione: false,
      canali_default: ['in_app'],
      giorni_evidenza: 21,
    },
    importante: {
      ordine: 2,
      label: 'importante',
      forza_presa_visione: false,
      canali_default: ['in_app'],
      giorni_evidenza: 30,
    },
    critico: {
      ordine: 1,
      label: 'critico',
      forza_presa_visione: true,
      canali_default: ['in_app'],
      giorni_evidenza: 45,
    },
  },
};

/**
 * Ordine di visualizzazione di due annunci: prima il livello più alto, poi il
 * più recente. Funzione pura così widget, pagina e console ordinano identico.
 */
export function confrontaPerEvidenza(
  a: { livello: KbLivelloImpatto; ordinamentoMs: number | null },
  b: { livello: KbLivelloImpatto; ordinamentoMs: number | null },
  governance: KbGovernance = KB_GOVERNANCE_DEFAULT
): number {
  const oa = governance.livelli_impatto[a.livello]?.ordine ?? 99;
  const ob = governance.livelli_impatto[b.livello]?.ordine ?? 99;
  if (oa !== ob) return oa - ob;
  return (b.ordinamentoMs ?? 0) - (a.ordinamentoMs ?? 0);
}
